import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from './useReducedMotion';

/**
 * Animates a number from its previous value to the current value
 * using requestAnimationFrame with ease-out easing.
 */
export const useCountUp = (target: number | null, duration = 400, decimals = 1): string => {
  const reduceMotion = useReducedMotion();
  const [display, setDisplay] = useState(target);
  const prevRef = useRef(target);
  const rafRef = useRef<number>();

  useEffect(() => {
    if (target === null) {
      setDisplay(null);
      prevRef.current = null;
      return;
    }

    const from = prevRef.current ?? target;
    prevRef.current = target;

    if (reduceMotion || from === target) {
      setDisplay(target);
      return;
    }

    const start = performance.now();
    const diff = target - from;

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out: 1 - (1 - t)^3
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + diff * eased);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, reduceMotion]);

  if (display === null) return '-';
  return display.toFixed(decimals);
};
